import Vue from "vue";
import CXMApiClient from "../../CXMApiClient";
import { arrayToObj } from "@/lib/utilities";
import { HelperFunctions } from "@/lib/utilities/HelperFunctions";
import { WorkflowCategory } from "@/lib/utilities/enums.js";

function initialState() {
  return {
    activityTypes: [],
    actionTypes: [],
    emailTemplates: [],
    processTemplates: [],
    ActivityAction: [],
    RuleTaskStatus: [
      { key: "Pending", value: "1" },
      { key: "InProgress", value: "2" },
      { key: "Overdue", value: "3" },
      { key: "Completed", value: "4" },
      { key: "Canceled", value: "5" }
    ], //[{key: 'Overdue', value: '3'}, {key: 'Completed', value: '4'}],
    RuleTaskAction: [
      { key: "Recycle", value: "1" },
      { key: "Reassign", value: "2" },
      { key: "Try Again", value: "3" },
      { key: "Move Forward", value: "4" }
    ],
    RuleLeadStatus: [
      { key: "On Start", value: "2" },
      { key: "Completed", value: "3" }
    ],
    TaskTypes: [],
    categories: [
      // TODO: Verify and pull from LookUp controller if available
      { id: 0, name: "None" },
      { id: 1, name: "O2O" },
      { id: 2, name: "Email" },
      { id: 3, name: "Phone" },
      { id: 4, name: "SMS" },
      { id: 5, name: "Online" },
      { id: 6, name: "On the Lot" },
      { id: 7, name: "SLM" },
      { id: 8, name: "Reviews" },
      { id: 9, name: "GeoAlert" },
      { id: 10, name: "AutoAssistant" }
    ],
    ChangeLogPagination: {
      pageTotal: 0,
      pageNumber: 0,
      pageSize: 0,
      totalRecords: 0
    },
    ChangeLogCurrentPage: 1,
    ChangeLogCurrentSize: 25,
    showSubscribeModal: false,
    showChangeLog: false,
    AllRuleGroups: [],
    RuleGroups: [],
    RulesAdminRoles: [], // Roles filtered based on BDC Enabled flag for the selected platformId
    CopyIndividualWorkflowModalInfo: {
      showModal: false,
      data: null
    },
    CopyAllWorkflowsModalInfo: {
      showModal: false,
      data: null
    },
    ResetWorkflowsModalInfo: {
      data: null,
      showModal: false
    }
  };
}

const state = initialState();

const getters = {
  categories(state) {
    return state.categories;
  },
  activityTypes(state) {
    return state.activityTypes;
  },
  activityMap(state) {
    return arrayToObj(state.activityTypes);
  },
  actionTypes(state) {
    return state.actionTypes;
  },
  actionMap(state) {
    return arrayToObj(state.actionTypes);
  },
  activityActionTypes(state, getters) {
    return getters.activityTypes.map(activity => ({
      ...activity,
      actions: getters.actionTypes.filter(
        action => action.activityTypeId == activity.id
      )
    }));
  },
  emailTemplates(state) {
    return state.emailTemplates;
  },
  processTemplates(state) {
    return state.processTemplates;
  },
  ActivityAction(state) {
    return state.ActivityAction;
  },
  AllActivityActions: function(state) {
    return state.AllActivityActions;
  },
  RuleTaskAction(state) {
    return state.RuleTaskAction;
  },
  RuleTaskStatus(state) {
    return state.RuleTaskStatus;
  },
  RuleLeadStatus(state) {
    return state.RuleLeadStatus;
  },
  TaskTypes(state) {
    return state.TaskTypes;
  },
  ChangeLogPagination: function(state) {
    return state.ChangeLogPagination;
  },
  ChangeLogCurrentPage: function(state) {
    return state.ChangeLogCurrentPage;
  },
  ChangeLogCurrentSize: function(state) {
    return state.ChangeLogCurrentSize;
  },
  showSubscribeModal: function() {
    return state.showSubscribeModal;
  },
  showChangeLog: function() {
    return state.showChangeLog;
  },
  RuleGroups: function() {
    return state.RuleGroups;
  },
  AllRuleGroups: function() {
    return state.AllRuleGroups;
  },
  FormattedActionTypes() {
    let formattedTypes = {};
    if (state.actionTypes && state.actionTypes.length > 0) {
      state.actionTypes.forEach(action => {
        const activityType = state.activityTypes.find(
          type => type.id == action.activityTypeId
        );
        const activityName = `${activityType ? activityType.name : ""}`.replace(
          /[_\W]*/g,
          ""
        );
        const actionName = `${action.name ? action.name : ""}`.replace(
          /[_\W]*/g,
          ""
        );
        const name = `${activityName}_${actionName}`;
        formattedTypes[name] = action.id;
      });
    }
    return formattedTypes;
  }
};

const actions = {
  getActivityTypes(context) {
    return new Promise(async (resolve, reject) => {
      let client = new CXMApiClient();
      resolve(
        await client
          .ProcessRequest("post", "/api/Activity/activitytypes")
          .then(response => {
            context.commit("activityTypes", response.data);
            if (state.actionTypes && state.actionTypes.length > 0) {
              context.commit("activityActionTypes");
            }
          })
          .catch(function(error) {
            reject(error);
          })
      );
    });
  },
  getActionTypes(context) {
    return new Promise(async (resolve, reject) => {
      let client = new CXMApiClient();
      resolve(
        await client
          .ProcessRequest("post", "/api/Activity/actiontypes")
          .then(response => {
            context.commit("actionTypes", response.data);
            if (state.activityTypes && state.activityTypes.length > 0) {
              context.commit("activityActionTypes");
            }
          })
          .catch(function(error) {
            reject(error);
          })
      );
    });
  },
  loadEmailTemplatesForRulesAdminPlatform(context, accountPlatformId) {
    return new Promise((resolve, reject) => {
      context.commit("setEmailTemplates", []);
      context
        .dispatch("getEmailTemplatesForPlatform", accountPlatformId)
        .then(response => {
          context.commit("setEmailTemplates", response);
          resolve(true);
        })
        .catch(err => {
          reject(false);
        });
    });
  },
  async loadProcessTemplatesForRulesAdminPlatform(context, accountPlatformId) {
    context.commit("setProcessTemplates", []);
    const requestPackage = {
      AccountPlatformIds: [accountPlatformId],
      CategoryIds: [WorkflowCategory.WorkflowTriggered],
      Status: 50
    };
    const response = await Vue.http
      .post("/pando/workflowtemplate/search", requestPackage)
      .catch(err => err.response);
    context.commit(
      "setProcessTemplates",
      response.data && response.data.Success ? response.data.Data : []
    );
  },
  getChangeLog(context, request) {
    return new Promise(async (resolve, reject) => {
      let client = new CXMApiClient();
      resolve(
        await client
          .ProcessRequest("post", "/api/Audit/changelog_by_table", request)
          .then(response => {
            if (response && response.data && response.data.data) {
              context.commit(
                "updateChangeLogPagination",
                response.data.data.pagination
              );
              return response.data.data.changeLog;
              // return response.data.data.changeLog.sort((a, b) => {
              //   a = new Date(a.actionTstamp);
              //   b = new Date(b.actionTstamp);
              //   return a > b ? -1 : a < b ? 1 : 0;
              // });
            }
          })
          .catch(function(error) {
            reject(error);
          })
      );
    });
  },
  getActivityAction(context) {
    return new Promise(async (resolve, reject) => {
      resolve(
        await Vue.http
          .get("pando/pandox/actiontypes")
          .then(response => {
            if (response.data.Success) {
              let templates =
                response.data.Data && response.data.Data.length > 0
                  ? response.data.Data.slice()
                  : [];

              context.dispatch("setActivityAction", templates);
              context.commit("updateAllActivityActions", templates);
              return { Success: response.data.Success, message: "" };
            } else {
              reject("Failed to retrieve Activity/Action Pairs.");
            }
          })
          .catch(err => {
            let message = err.message ? err.message : err;
            return { Success: false, Message: message };
          })
      );
    }).catch(err => {
      let message = err.message ? err.message : err;
      return { Success: false, Message: message };
    });
  },
  setActivityAction(context, val) {
    context.commit("ActivityAction", val);
  },
  saveAllActions(context, val) {
    return new Promise(async (resolve, reject) => {
      resolve(
        await Vue.http
          .post("pando/pandox/saveactiontypes", val)
          .then(r => {
            if (r.data.Success) {
              return { Success: true, message: "" };
            }
            reject("There was a problem with the save");
          })
          .catch(err => {
            let message = err.message ? err.message : err;
            return { Success: false, Message: message };
          })
      );
    }).catch(err => {
      let message = err.message ? err.message : err;
      return { Success: false, Message: message };
    });
  },
  getVehicleMakes(context, years) {
    // TODO: Compare with vehicle filters and use that action if it works
    return new Promise(async (resolve, reject) => {
      let client = new CXMApiClient();
      resolve(
        await client
          .ProcessRequest(
            "post",
            "/crm/Lookup/vehicle/makes",
            JSON.stringify(years)
          )
          .then(response => {
            return response.data.data;
          })
          .catch(function(error) {
            reject(error);
          })
      );
    });
  },
  getVehicleFullModels(context, dto) {
    // TODO: Compare with vehicle filters and use that action if it works
    return new Promise(async (resolve, reject) => {
      let client = new CXMApiClient();
      resolve(
        await client
          .ProcessRequest(
            "post",
            "/crm/Lookup/vehicle/models",
            JSON.stringify(dto)
          )
          .then(response => {
            return response.data.data;
          })
          .catch(function(error) {
            reject(error);
          })
      );
    });
  },
  getVehicleTrims(context, dto) {
    // TODO: Compare with vehicle filters and use that action if it works
    return new Promise(async (resolve, reject) => {
      let client = new CXMApiClient();
      resolve(
        await client
          .ProcessRequest(
            "post",
            "/crm/Lookup/vehicle/trims",
            JSON.stringify(dto)
          )
          .then(response => {
            return response.data.data;
          })
          .catch(function(error) {
            reject(error);
          })
      );
    });
  },
  getTaskTypes(context) {
    if (state.TaskTypes.length > 0) {
      return { Success: true };
    }
    return new Promise(async (resolve, reject) => {
      let client = new CXMApiClient();
      resolve(
        await client
          .ProcessRequest("get", "/crm/Lookup/tasktypes")
          .then(responseTypes => {
            if (responseTypes.status === 200) {
              context.commit("setTaskTypes", responseTypes.data.data);
              return { Success: true };
            } else {
              reject("Failed to retrieve tasks types");
            }
          })
          .catch(err => {
            let message = err.message ? err.message : err;
            return { Success: false, Message: message };
          })
      );
    }).catch(function(err) {
      let message = err.message ? err.message : err;
      return { Success: false, Message: message };
    });
  },
  updateChangeLogCurrentPage: function(context, page) {
    context.commit("updateChangeLogCurrentPage", page);
  },
  updateChangeLogCurrentSize: function(context, size) {
    context.commit("updateChangeLogCurrentSize", size);
  },
  updateChangeLogPagination: function(context, data) {
    context.commit("updateChangeLogPagination", data);
  },
  toggleSubscribeModal: function(context, data) {
    //context.commit("toggleChangeLogModal", false);
    context.commit("toggleSubscribeModal", data);
  },
  toggleChangeLogModal: function(context, data) {
    context.commit("toggleChangeLogModal", data);
  },
  getRuleGroups: async function(context, platformId) {
    const response = await Vue.http
      .get("pando/pandox/rulelist/grouped?accountPlatformId=" + platformId)
      .catch(err => err.response);
    if (response && response.data && response.data.Success) {
      context.commit("updateRuleGroups", response.data.Data);
      context.commit("updateAllRuleGroups", response.data.Data);
    }
    return response;
  },
  updateRuleGroups: function(context, data) {
    context.commit("updateRuleGroups", data);
  },
  getRulesAdminRolesForPlatform(context, platformId) {
    Vue.http
      .get(`/rolepermissions/getalertminerrolesforrulesadmin/${platformId}`)
      .then(resp => {
        let response =
          resp && resp.data && resp.data.Data ? resp.data.Data : [];
        context.commit("gotRulesAdminRolesForPlatform", response);
      })
      .catch(resp => {
        context.commit("gotRulesAdminRolesForPlatform", []);
      });
  },
  loadFilterValues: function(context, filterId) {
    return new Promise(async (resolve, reject) => {
      let client = new CXMApiClient();
      resolve(
        await client
          .ProcessRequest("put", "/api/TriggerSet/filterValues", filterId)
          .then(response => {
            if (
              response &&
              response.data &&
              response.data.successful &&
              response.data.data
            ) {
              resolve(response.data.data);
            } else {
              reject(response);
            }
          })
          .catch(error => reject(error))
      );
    });
  },
  loadVehicleFilterValues: function(context, request) {
    let client = new CXMApiClient();

    return new Promise((resolve, reject) => {
      client
        .ProcessRequest("put", "/api/TriggerSet/filterValues/vehicle", request)
        .then(response => {
          if (response && response.data && response.data.data)
            resolve(response.data.data);
          else reject(response);
        })
        .catch(error => reject(error));
    });
  },
  loadVehicleFilterModels: function(context, request) {
    let client = new CXMApiClient();

    return new Promise((resolve, reject) => {
      client
        .ProcessRequest(
          "put",
          "/api/TriggerSet/filterValues/vehicle/models",
          request
        )
        .then(response => {
          if (response && response.data && response.data.data)
            resolve(
              response.data.data.sort(
                HelperFunctions.sortByPropertyAsc("modelName")
              )
            );
          else reject(response);
        })
        .catch(error => reject(error));
    });
  },
  loadVehicleFilterTrims: function(context, modelId) {
    let client = new CXMApiClient();

    return new Promise((resolve, reject) => {
      client
        .ProcessRequest(
          "put",
          "/api/TriggerSet/filterValues/vehicle/trims?modelId=" + modelId
        )
        .then(response => {
          if (response && response.data && response.data.data)
            resolve(
              response.data.data.sort(
                HelperFunctions.sortByPropertyAsc("trimName")
              )
            );
          else reject(response);
        })
        .catch(error => reject(error));
    });
  },
  updateCopyIndividualWorkflowModalInfo(context, data) {
    context.commit("updateCopyIndividualWorkflowModalInfo", data);
  },
  async copyWorkflow(context, request) {
    let response = await Vue.http
      .post(
        "/pando/pandox/triggers/copy_trigger_set/" + request.triggerSetId,
        request.request
      )
      .catch(ex => {
        return null;
      });
    return response && response.data ? response.data.Data : null;
  },
  updateCopyAllWorkflowsModalInfo(context, data) {
    context.commit("updateCopyAllWorkflowsModalInfo", data);
  },
  async copyAllWorkflows(context, request) {
    let response = await Vue.http
      .post("/pando/pandox/triggers/copy_trigger_sets/queuerequest", request)
      .catch(ex => {
        return null;
      });
    return response && response.data ? response.data.Data : null;
  },
  updateResetWorkflowsModalInfo(context, data) {
    context.commit("updateResetWorkflowsModalInfo", data);
  },
  async resetWorkflows(context, request) {
    let response = await Vue.http
      .post("/pando/pandox/triggers/reset_trigger_set", request)
      .catch(ex => {
        return null;
      });
    return response && response.data ? response.data.Data : null;
  }
};

const mutations = {
  activityActionTypes(state) {
    let actions = state.actionTypes;
    let activities = state.activityTypes;
    actions.forEach(action => {
      let activity = activities.find(
        activity => activity.id === action.activityTypeId
      );
      action.activityName = activity.name;
    });
    let aaPairs = [];
    actions.forEach(aat => {
      let pair = {
        name: aat.activityName + " - " + aat.name,
        id: aat.id,
        $isDisabled: false
      };
      aaPairs.push(pair);
    });
  },

  activityTypes(state, types) {
    if (types && types.length > 0) {
      types.sort((type1, type2) =>
        type1.name &&
        type2.name &&
        type1.name.toLowerCase() > type2.name.toLowerCase()
          ? 1
          : -1
      );
      state.activityTypes = types;
    } else state.activityTypes = [];
  },
  actionTypes(state, types) {
    (types || []).sort((type1, type2) => (type1.id > type2.id ? 1 : -1));
    state.actionTypes = types;
  },
  setEmailTemplates(state, templates) {
    if (templates && templates.length > 0)
      templates.sort((a, b) => {
        return a.TemplateName.toLowerCase() > b.TemplateName.toLowerCase()
          ? 1
          : -1;
      });
    state.emailTemplates = templates;
  },
  setProcessTemplates(state, templates) {
    if (templates && templates.length > 0)
      templates.sort((a, b) => {
        return a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1;
      });
    state.processTemplates = templates;
  },
  ActivityAction(state, val) {
    state.ActivityAction = JSON.parse(JSON.stringify(val));
  },
  updateAllActivityActions: function(state, val) {
    state.AllActivityActions = JSON.parse(JSON.stringify(val));
  },
  setTaskTypes(state, val) {
    state.TaskTypes = val;
  },
  updateChangeLogPagination: function(state, data) {
    state.ChangeLogPagination = data;
  },
  updateChangeLogCurrentPage: function(state, page) {
    state.ChangeLogCurrentPage = page;
  },
  updateChangeLogCurrentSize: function(state, size) {
    state.ChangeLogCurrentSize = size;
  },
  toggleSubscribeModal: function(state, data) {
    state.showSubscribeModal = data;
  },
  toggleChangeLogModal: function(state, data) {
    state.showChangeLog = data;
  },
  updateRuleGroups: function(state, data) {
    state.RuleGroups = data;
  },
  updateAllRuleGroups: function(state, data) {
    state.AllRuleGroups = (data || []).sort((a, b) =>
      (a.name || "").trim().toLowerCase() > (b.name || "").trim().toLowerCase()
        ? 1
        : -1
    );
  },
  gotRulesAdminRolesForPlatform(state, data) {
    state.RulesAdminRoles = data ? data : [];
  },
  updateCopyIndividualWorkflowModalInfo(state, data) {
    state.CopyIndividualWorkflowModalInfo = data;
  },
  updateCopyAllWorkflowsModalInfo(state, data) {
    state.CopyAllWorkflowsModalInfo = data;
  },
  updateResetWorkflowsModalInfo(state, data) {
    state.ResetWorkflowsModalInfo = data;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/rules/rules.js